import axios from 'axios';
import { apiClient } from './client';

export interface AuthUser {
  id:          string;
  email:       string;
  full_name:   string;
  role:        'admin' | 'user';
  tenant_id:   string;
  tenant_name: string;
}

export interface LoginResponse {
  access_token:  string;
  refresh_token: string;
  user:          AuthUser;
}

export interface LoginTotpRequired {
  totp_required: true;
  totp_challenge: string;
}

export type LoginResult = LoginResponse | LoginTotpRequired;

export async function login(email: string, password: string): Promise<LoginResult> {
  const res = await axios.post<LoginResult>('/api/v1/auth/login', { email, password });
  return res.data;
}

export async function verifyTotp(args: {
  totp_challenge: string;
  code?: string;
  backup_code?: string;
}): Promise<LoginResponse> {
  const res = await axios.post<LoginResponse>('/api/v1/auth/login/totp', args);
  return res.data;
}

export async function setup2FA(): Promise<{ secret: string; otpauth_url: string }> {
  const res = await apiClient.post<{ secret: string; otpauth_url: string }>('/auth/2fa/setup');
  return res.data;
}

export async function enable2FA(code: string): Promise<{ ok: true; backup_codes: string[] }> {
  const res = await apiClient.post<{ ok: true; backup_codes: string[] }>('/auth/2fa/enable', { code });
  return res.data;
}

export async function disable2FA(password: string, code: string): Promise<{ ok: true }> {
  const res = await apiClient.post<{ ok: true }>('/auth/2fa/disable', { password, code });
  return res.data;
}

export async function get2FAStatus(): Promise<{
  totp_enabled: boolean;
  totp_enabled_at: string | null;
  backup_codes_remaining: number;
}> {
  const res = await apiClient.get<{ totp_enabled: boolean; totp_enabled_at: string | null; backup_codes_remaining: number }>('/auth/2fa/status');
  return res.data;
}

export async function logout(refreshToken: string): Promise<void> {
  await apiClient.post('/auth/logout', { refresh_token: refreshToken }).catch(() => {});
}

export async function getMe(): Promise<AuthUser> {
  const res = await apiClient.get<AuthUser>('/auth/me');
  return res.data;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiClient.put('/auth/change-password', {
    current_password: currentPassword,
    new_password:     newPassword,
  });
}

export async function updateProfile(data: { full_name?: string; email?: string }): Promise<AuthUser> {
  const res = await apiClient.put<AuthUser>('/auth/profile', data);
  return res.data;
}
